"use client"

import { SOURCE_FILTER_ROW, sourceFilterPill } from "@/components/source-filter-pill"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ChevronDown } from "@/lib/icons"

export type HitomiLanguage = "" | "english" | "japanese" | "chinese" | "korean"
export type HitomiType = "" | "doujinshi" | "manga" | "artistcg" | "gamecg" | "imageset"

interface HitomiFilterTabsProps {
  language: HitomiLanguage
  type: HitomiType
  onLanguageChange: (value: HitomiLanguage) => void
  onTypeChange: (value: HitomiType) => void
}

// Hitomi keeps one list of galleries per language and per type (index-<language>.nozomi, type/<type>-all.nozomi, checked 2026-10-23), so
// these narrow the search itself rather than hiding results after the fact. Both rows combine: English + Doujinshi shows only English doujinshi.
const LANGUAGES: { value: HitomiLanguage; label: string }[] = [
  { value: "", label: "All" },
  { value: "english", label: "English" },
  { value: "japanese", label: "Japanese" },
  { value: "chinese", label: "Chinese" },
  { value: "korean", label: "Korean" },
]

const TYPES: { value: HitomiType; label: string }[] = [
  { value: "", label: "All" },
  { value: "doujinshi", label: "Doujinshi" },
  { value: "manga", label: "Manga" },
  { value: "artistcg", label: "Artist CG" },
  { value: "gamecg", label: "Game CG" },
  { value: "imageset", label: "Image Set" },
]

/** The Hitomi tags for the two choices, written the way lib/hitomi-tags.ts reads them ("language:english type:manga"). */
export function hitomiFilterTags(language: HitomiLanguage, type: HitomiType): string {
  return [language && `language:${language}`, type && `type:${type}`].filter(Boolean).join(" ")
}

// Two dropdowns on ONE row (like Rule34's strip), not two rows of pills: eleven pills took two lines of the bar. Each button shows what is
// chosen and lights up like an active pill when something other than "All" is picked.
function FilterDropdown<T extends string>({ title, value, options, onChange }: { title: string; value: T; options: { value: T; label: string }[]; onChange: (value: T) => void }) {
  const current = options.find((opt) => opt.value === value)
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className={`${sourceFilterPill(value !== "")} inline-flex items-center gap-1`}>
          {title}: {value === "" ? "All" : current?.label}
          <ChevronDown className="h-3 w-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[9rem]">
        <DropdownMenuRadioGroup value={value} onValueChange={(next) => onChange(next as T)}>
          {options.map((opt) => (
            <DropdownMenuRadioItem key={opt.value || "all"} value={opt.value}>
              {opt.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function HitomiFilterTabs({ language, type, onLanguageChange, onTypeChange }: HitomiFilterTabsProps) {
  return (
    <div className={SOURCE_FILTER_ROW}>
      <FilterDropdown title="Language" value={language} options={LANGUAGES} onChange={onLanguageChange} />
      <FilterDropdown title="Type" value={type} options={TYPES} onChange={onTypeChange} />
    </div>
  )
}
